import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material"
import { useRef, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { useRefresh } from "lib/@hooks/useRefresh"
import { If } from "lib/@components/if"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { TaskRowEntry } from "routes/schedule/tasks/task-row-entry"
import { TaskDetailsEntry } from "routes/schedule/tasks/task-details-entry"
import { VALID_CLASSIFICATIONS } from "library/SFG20/constants.mjs"
import { ProposedTaskEditForm } from "./proposed-task-edit-form"

export function ProposedTaskCard({ proposedTask, onTaskChange }) {
    const [editMode, setEditMode] = useState(false)
    const originalTask = useRef({ ...proposedTask })
    const taskRef = useRef({ ...proposedTask })
    const refresh = useRefresh()

    const isValidClassification = VALID_CLASSIFICATIONS.includes(taskRef.current.classification)

    return (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    px: 1.5,
                    py: 0.75,
                    bgcolor: "background.neutral",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight="medium">
                        Proposed new task
                    </Typography>
                    <If truthy={isValidClassification}>
                        <Chip
                            label={taskRef.current.classification}
                            size="small"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                        />
                    </If>
                    <If truthy={taskRef.current.minutes > 0}>
                        <Tooltip title={taskRef.current.minutesRationale || ""} placement="bottom" arrow>
                            <Chip
                                label={`${taskRef.current.minutes}m`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 18, fontSize: "0.65rem" }}
                            />
                        </Tooltip>
                    </If>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <If truthy={editMode}>
                        <Button size="small" variant="outlined" onClick={cancelEdit}>
                            Cancel
                        </Button>
                        <Button size="small" variant="contained" onClick={saveEdit}>
                            Save
                        </Button>
                        <Else>
                            <Button
                                size="small"
                                variant="text"
                                onClick={startEdit}
                                startIcon={<Iconify icon="mdi:pencil-outline" sx={{ fontSize: "14px !important" }} />}
                                sx={{ fontSize: "0.75rem" }}
                            >
                                Edit
                            </Button>
                        </Else>
                    </If>
                </Stack>
            </Stack>

            <Box sx={{ px: 1.5, py: 1 }}>
                <If truthy={editMode}>
                    <Bound target={taskRef.current} refresh={refresh}>
                        <ProposedTaskEditForm />
                    </Bound>
                    <Else>
                        <Bound
                            target={taskRef.current}
                            task={taskRef.current}
                            editMode={false}
                            hideDifference={true}
                            refresh={refresh}
                        >
                            <TaskRowEntry
                                classification={taskRef.current.classification}
                                headerSx={{ mt: 0, width: 40, whiteSpace: "normal" }}
                                titleDetails={{ whiteSpace: "normal" }}
                            />
                            <TaskDetailsEntry
                                item={taskRef.current}
                                sx={{ fontSize: 14 }}
                                containerSx={{ mt: 0.5, mb: 1, pl: 2 }}
                            />
                        </Bound>
                    </Else>
                </If>
            </Box>
        </Box>
    )

    function startEdit() {
        setEditMode(true)
    }

    function cancelEdit() {
        Object.assign(taskRef.current, { ...originalTask.current })
        setEditMode(false)
        refresh()
    }

    function saveEdit() {
        originalTask.current = { ...taskRef.current }
        setEditMode(false)
        onTaskChange?.({ ...taskRef.current })
    }
}
